import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from './enums/app-sdk';
import { randomFuncName } from './tools';

/* TODO:
1. 命名不规范，大小写都有，应统一使用驼峰命名且首字母大写，需要推动APP 统一更改。
2. 部分通道已经废弃，前端需要同步废弃。  ClearChoosedImg、RemoveOneChoosedImg
*/

// 类型定义
type CallbackFunction = (data: any) => void | Promise<void>;
type ListenerFunction = (data: any) => void;

// 扩展 Window 接口以支持动态属性
declare global {
  interface Window {
    [key: string]: any;
  }
}

/**
 * App通信管理器
 * 用于统一管理h5和app之间的通信事件
 */
class AppCommunicationManager {
  private callbacks: Map<string, CallbackFunction>;
  private listeners: Map<string, ListenerFunction>;

  constructor() {
    this.callbacks = new Map<string, CallbackFunction>(); // 存储回调函数
    this.listeners = new Map<string, ListenerFunction>(); // 存储监听器
  }

  /**
   * 注册回调通信（双向通信）
   * @param {EAppCommunicationFuncName} eventName - 事件名称
   * @param {CallbackFunction} callback - 回调函数
   * @param {Record<string, any>} messageData - 发送给app的数据
   * @param {EAppCommunicationFuncName} postMessageName - 必填，指定postMessage方法名
   * @returns {string} 回调函数名
   */
  registerCallback(
    eventName: EAppCommunicationFuncName,
    callback: CallbackFunction,
    messageData: Record<string, any> = {},
    postMessageName: EAppCommunicationFuncName,
  ): string {
    if (!postMessageName) {
      throw new Error('postMessageName is required');
    }

    // TODO:生成随机回调函数名,可能有些场景不需要随机？
    const callbackFnName = randomFuncName(eventName);

    // 存储回调函数
    this.callbacks.set(callbackFnName, callback);

    // 注册window回调函数
    (window as any)[callbackFnName] = async (data: any) => {
      try {
        logger.log('appCallback+++++++++', eventName, postMessageName);
        await callback(data);
      } catch (error) {
        logger.error(`Callback error for ${eventName}:`, error);
      }
    };

    // 发送消息给app
    const message = {
      Callback: callbackFnName,
      ...messageData,
    };

    if (postMessageName && (window as any)[postMessageName]) {
      (window as any)[postMessageName].postMessage(JSON.stringify(message));
    }

    return callbackFnName;
  }

  /**
   * 注册监听器（单向通信）
   * @param {EAppCommunicationFuncName} eventName - 事件名称
   * @param {ListenerFunction} listener - 监听函数
   */
  registerListener(eventName: EAppCommunicationFuncName, listener: ListenerFunction): void {
    // 存储监听器
    this.listeners.set(eventName, listener);

    // 注册window监听函数
    (window as any)[eventName] = (data: any) => {
      try {
        logger.log('appEventListener+++++++++', eventName);
        listener(data);
      } catch (error) {
        logger.error(`Listener error for ${eventName}:`, error);
      }
    };
  }

  /**
   * 发送消息给app
   * @param {EAppCommunicationFuncName} postMessageName - postMessage的名称
   * @param {Record<string, any> | string} data - 发送的数据
   */
  sendMessage(
    postMessageName: EAppCommunicationFuncName,
    data: Record<string, any> | string,
  ): void {
    if ((window as any)[postMessageName]) {
      // 如果数据是字符串，直接发送；否则JSON.stringify
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      (window as any)[postMessageName].postMessage(message);
    } else {
      logger.warn(`${postMessageName} not found on window`);
    }
  }

  /**
   * 移除回调函数
   * @param {string} callbackFnName - 回调函数名
   */
  removeCallback(callbackFnName: string): void {
    if (this.callbacks.has(callbackFnName)) {
      this.callbacks.delete(callbackFnName);
      delete (window as any)[callbackFnName];
    }
  }

  /**
   * 移除监听器
   * @param {EAppCommunicationFuncName} eventName - 事件名称
   */
  removeListener(eventName: EAppCommunicationFuncName): void {
    if (this.listeners.has(eventName)) {
      this.listeners.delete(eventName);
      delete (window as any)[eventName];
    }
  }

  /**
   * 清理所有回调和监听器
   */
  cleanup(): void {
    // 清理回调函数
    this.callbacks.forEach((callback, fnName) => {
      delete (window as any)[fnName];
    });
    this.callbacks.clear();

    // 清理监听器
    this.listeners.forEach((listener, eventName) => {
      delete (window as any)[eventName];
    });
    this.listeners.clear();
  }

  /**
   * 获取所有已注册的回调函数名
   */
  getCallbacks(): string[] {
    return Array.from(this.callbacks.keys());
  }

  /**
   * 获取所有已注册的监听器名
   */
  getListeners(): string[] {
    return Array.from(this.listeners.keys());
  }

  /**
   * 检查回调函数是否存在
   */
  hasCallback(callbackFnName: string): boolean {
    return this.callbacks.has(callbackFnName);
  }

  /**
   * 检查监听器是否存在
   */
  hasListener(eventName: EAppCommunicationFuncName): boolean {
    return this.listeners.has(eventName);
  }

  /**
   * 注册 APP 进入离开监听
   */
  registerAppEnterLeaveListener() {
    try {
      const appFuncName = 'ReceiveInAppWebCommonFunc';
      const viewCallbackFnName = randomFuncName(`${appFuncName}OnPageView`);
      const closeCallbackFnName = randomFuncName(`${appFuncName}OnPageClose`);
      const getSensorInfoFnName = randomFuncName(`${appFuncName}ReturnSensorInfo`);
      const _params = {
        onPageView: viewCallbackFnName,
        onPageClose: closeCallbackFnName,
        returnSensorInfo: getSensorInfoFnName,
      };
      (window as any)[viewCallbackFnName] = (res) => {
        const _res = {
          refer_page_name: '',
          refer_url_path: '',
        };
        try {
          if (typeof res === 'object') {
            _res.refer_page_name = res.refer_page_name;
            _res.refer_url_path = res.refer_url_path;
            sessionStorage.setItem(
              'zl-from-route-info',
              JSON.stringify({ ..._res, refer_path_name: '' }),
            );
            logger.log('appView data+++++++++', _res);
          }
        } catch (err) {
          logger.error('appView error:', err);
        }
        const appView = new CustomEvent('appView', {
          detail: _res,
        });
        window.dispatchEvent(appView);
      };
      (window as any)[closeCallbackFnName] = (res) => {
        const appLeave = new CustomEvent('appLeave', {
          detail: res,
        });
        window.dispatchEvent(appLeave);
      };
      (window as any)[getSensorInfoFnName] = (res) => {
        sessionStorage.setItem('zl-distinct_id', res.distinctId);
        sessionStorage.setItem('zl-anonymous_id', res.anonymousId);
      };
      window[appFuncName] && window[appFuncName].postMessage(JSON.stringify(_params));
    } catch (error) {
      logger.error('registerAppEnterLeaveListener error:', error);
    }
  }
}

// 创建单例实例
const appCommunicationManager = new AppCommunicationManager();

export default appCommunicationManager;

// 导出类型定义
export { EAppCommunicationFuncName };
export type { CallbackFunction, ListenerFunction };
